import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-gray-950">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-white mb-4">Terms & Conditions</h1>
          <p className="text-gray-400 mb-8">Last Updated: January 2025</p>
          
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 space-y-8 text-gray-300">
              
              <p className="leading-relaxed text-lg">
                These Terms & Conditions ("Terms") govern your access to and use of the Traain platform, website, and services (collectively, the "Services") operated by Traain ("Traain," "we," "our," or "us").
              </p>
              <p className="leading-relaxed text-lg font-semibold text-white">
                By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, do not use the Services.
              </p>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">1. Use of the Services</h2>
                <p className="leading-relaxed mb-4">
                  Traain provides an AI-powered training, role-play, and enablement platform intended for business and enterprise use.
                </p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">You agree to:</h3>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>Use the Services only for lawful business purposes</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Ensure that users under your account comply with these Terms</li>
                </ul>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">You may not:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Reverse engineer, copy, or resell the platform</li>
                  <li>Use the Services to infringe on intellectual property or privacy rights</li>
                  <li>Introduce malicious code or attempt unauthorized access</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">2. Accounts and Access</h2>
                <p className="leading-relaxed mb-4">Access to certain features requires an account.</p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">You are responsible for:</h3>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>Maintaining the confidentiality of account credentials</li>
                  <li>All activity occurring under your account</li>
                  <li>Promptly notifying Traain of unauthorized use</li>
                </ul>
                
                <p className="leading-relaxed">Traain reserves the right to suspend or terminate accounts that violate these Terms.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">3. Customer Content and Data</h2>
                <p className="leading-relaxed mb-4">
                  Customers retain ownership of all content they upload or create on the platform, including training materials, documentation, and performance data ("Customer Content").
                </p>
                <p className="leading-relaxed mb-4">
                  By using the Services, you grant Traain a limited, non-exclusive license to process Customer Content solely for the purpose of delivering the Services.
                </p>
                <p className="leading-relaxed font-semibold text-white">Traain does not claim ownership of Customer Content.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">4. AI Features and Role-Play Simulations</h2>
                <p className="leading-relaxed mb-4">
                  Traain uses AI-powered tools, including Navigator AI™, to generate training experiences, simulations, feedback, and performance insights.
                </p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">You acknowledge that:</h3>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>AI outputs are intended to support training and development—not replace human judgment</li>
                  <li>Performance insights are informational and should be interpreted within organizational context</li>
                  <li>AI-generated content may vary and is not guaranteed to be error-free</li>
                </ul>
                
                <p className="leading-relaxed">Traain is not responsible for decisions made solely based on AI-generated outputs.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">5. Intellectual Property</h2>
                <p className="leading-relaxed mb-4">
                  All Traain software, technology, branding, trademarks, and proprietary materials are owned by Traain or its licensors and are protected by intellectual property laws.
                </p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">Except as expressly permitted, you may not:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Copy, modify, or distribute Traain materials</li>
                  <li>Use Traain branding without written permission</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">6. Fees and Payment</h2>
                <p className="leading-relaxed mb-4">
                  Fees, billing terms, and subscription details are governed by your order form, agreement, or pricing plan.
                </p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">Unless otherwise stated:</h3>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>Fees are non-refundable</li>
                  <li>Payment obligations are binding once services begin</li>
                </ul>
                
                <p className="leading-relaxed">Traain reserves the right to suspend Services for non-payment.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">7. Confidentiality</h2>
                <p className="leading-relaxed mb-4">
                  Each party agrees to protect the other party's confidential information and use it only as necessary to perform under these Terms.
                </p>
                <p className="leading-relaxed">
                  Confidential information does not include information that is publicly available or lawfully obtained from a third party.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">8. Security and Availability</h2>
                <p className="leading-relaxed mb-4">
                  Traain implements reasonable safeguards to protect the platform and customer data. However, uninterrupted service is not guaranteed.
                </p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">Traain may:</h3>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>Perform maintenance</li>
                  <li>Update features</li>
                  <li>Modify or discontinue parts of the Services</li>
                </ul>
                
                <p className="leading-relaxed">With reasonable notice when practicable.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">9. Disclaimer of Warranties</h2>
                <p className="leading-relaxed mb-4">
                  The Services are provided "as is" and "as available."
                </p>
                <p className="leading-relaxed mb-4">
                  Traain disclaims all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </p>
                <p className="leading-relaxed">
                  We do not guarantee that the Services will meet all requirements or operate without interruption or errors.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">10. Limitation of Liability</h2>
                <p className="leading-relaxed mb-4">To the maximum extent permitted by law:</p>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>Traain is not liable for indirect, incidental, or consequential damages</li>
                  <li>Traain's total liability shall not exceed the amount paid to Traain in the 12 months preceding the claim</li>
                </ul>
                <p className="leading-relaxed">This applies regardless of the legal theory asserted.</p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">11. Indemnification</h2>
                <p className="leading-relaxed mb-4">You agree to indemnify and hold harmless Traain from claims arising out of:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Your use of the Services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your Customer Content</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">12. Termination</h2>
                <p className="leading-relaxed mb-4">
                  Either party may terminate access in accordance with applicable agreements.
                </p>
                
                <h3 className="text-xl font-semibold text-cyan-400 mb-2">Upon termination:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Access to the Services will end</li>
                  <li>Customer Content may be deleted in accordance with data retention policies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">13. Governing Law</h2>
                <p className="leading-relaxed">
                  These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">14. Changes to These Terms</h2>
                <p className="leading-relaxed">
                  Traain may update these Terms from time to time. Continued use of the Services after changes constitutes acceptance of the updated Terms.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold text-white mb-4">15. Contact Information</h2>
                <p className="leading-relaxed mb-4">
                  For questions about these Terms, contact:
                </p>
                <div className="p-4 bg-gray-800 rounded-lg">
                  <p className="font-semibold text-white">Traain</p>
                  <p>Email: <a href="mailto:support@traain.com" className="text-cyan-400 hover:text-cyan-300">support@traain.com</a></p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default TermsConditionsPage;
